/**
 * Utility to configure decimal conversion behavior for API requests
 */

/**
 * Centralized decimal precision configuration
 * Controls the number of decimal places used throughout the application
 */
export const ACCEPT_DECIMAL = 4;

/**
 * Currency decimal precision
 * Standard 2 decimal places for currency/monetary values (USD, taxes, fees, etc.)
 */
export const CURRENCY_DECIMAL = 2;

/**
 * Cap Table decimal precision.
 * Supports micro-par corporate structures (e.g. $0.00000001).
 * Backend stores as DECIMAL(18, 8).
 */
export const CAP_TABLE_DECIMAL = 8;

export interface DecimalConversionConfig {
  /** Skip decimal conversion entirely for this request */
  skipDecimalConversion?: boolean;
  /** Array of field paths to EXCLUDE from conversion (keep raw values) */
  excludeDecimalFields?: string[];
}

/**
 * URL-based shortcut — interceptor applies these automatically (no per-page axios wrapper).
 * Prefer specific field excludes over skip-all when possible.
 * Explicit request `__decimalConfig` / handleRequest wrappers still win when set.
 */
export const DECIMAL_ENDPOINT_RULES: ReadonlyArray<{
  /** Substring matched against `config.url` (e.g. `'/api/cap-table/api/app/securities'`) */
  match: string;
  skipDecimalConversion?: boolean;
  excludeDecimalFields?: string[];
}> = [
    {
      match: '/api/cap-table/api/app/securities',
      excludeDecimalFields: ['items[].sharePrice', 'sharePrice'],
    },
    {
      match: '/api/cap-table/api/app/charter-filing/stock-class-list-for-charter',
      excludeDecimalFields: ['items[].parValue', 'items[].originalIssuePrice', 'items[].dividendRate', 'parValue', 'originalIssuePrice', 'dividendRate'],
    },
  ];

/**
 * Normalize axios `config.url` (relative or absolute) to a comparable path segment.
 */
export function normalizeUrlPath(url: string): string {
  const withoutQuery = url.split('?')[0]?.trim() ?? '';
  if (!withoutQuery) return '';

  if (/^https?:\/\//i.test(withoutQuery)) {
    try {
      return new URL(withoutQuery).pathname.replace(/^\/+/, '');
    } catch {
      return withoutQuery.replace(/^https?:\/\/[^/]+/i, '').replace(/^\/+/, '');
    }
  }

  return withoutQuery.replace(/^\/+/, '');
}

function buildRequestUrl(config: { url?: string; baseURL?: string }): string | undefined {
  const url = config.url?.trim();
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;

  const base = config.baseURL?.trim().replace(/\/+$/, '');
  if (!base) return url;

  const path = url.startsWith('/') ? url : `/${url}`;
  if (/^https?:\/\//i.test(base)) {
    return `${base}${path}`;
  }

  return `${base}${path}`.replace(/\/+/g, '/').replace(/^\//, '');
}

function urlMatchesRule(normalizedUrl: string, normalizedMatch: string): boolean {
  return (
    normalizedUrl === normalizedMatch ||
    normalizedUrl.startsWith(`${normalizedMatch}/`) ||
    normalizedUrl.endsWith(`/${normalizedMatch}`) ||
    normalizedUrl.includes(`/${normalizedMatch}/`)
  );
}

function getDecimalConfigForUrl(url?: string): DecimalConversionConfig {
  if (!url) return {};
  const normalizedUrl = normalizeUrlPath(url);
  for (const rule of DECIMAL_ENDPOINT_RULES) {
    const normalizedMatch = normalizeUrlPath(rule.match);
    if (urlMatchesRule(normalizedUrl, normalizedMatch)) {
      return {
        skipDecimalConversion: rule.skipDecimalConversion,
        excludeDecimalFields: rule.excludeDecimalFields,
      };
    }
  }
  return {};
}

/**
 * Resolve effective decimal config for a request.
 * Request-level config (handleRequest / `__decimalConfig`) overrides URL rules.
 */
export const resolveDecimalConfig = (request: {
  url?: string;
  baseURL?: string;
  __decimalConfig?: DecimalConversionConfig;
}): DecimalConversionConfig => {
  const fromRequest = request?.__decimalConfig ?? {};

  if (fromRequest.skipDecimalConversion) {
    return {
      skipDecimalConversion: true,
      excludeDecimalFields: fromRequest.excludeDecimalFields,
    };
  }

  const fromUrl = getDecimalConfigForUrl(buildRequestUrl(request));
  return {
    skipDecimalConversion: fromUrl.skipDecimalConversion,
    excludeDecimalFields: fromRequest.excludeDecimalFields ?? fromUrl.excludeDecimalFields,
  };
};

// FIFO queue to store pending decimal configs
// This ensures the correct config is applied even when multiple requests are fired concurrently
const pendingDecimalConfigs: Array<DecimalConversionConfig | null> = [];

/**
 * Attach decimal config from promise to be used by the next axios request
 * This is called by handleRequest hooks before awaiting the promise
 */
export const attachDecimalConfigToRequest = (request: Promise<any>): void => {
  // Extract config from the promise
  const config = (request as any).__decimalConfig as DecimalConversionConfig | undefined;

  // Push config (or null placeholder) to maintain ordering with axios requests
  pendingDecimalConfigs.push(config ?? null);
};

/**
 * Get and consume the decimal config for the next request
 * Called by axios request interceptor to attach config to axios config object
 */
export const consumeNextRequestConfig = (): DecimalConversionConfig | null => {
  if (pendingDecimalConfigs.length === 0) {
    return null;
  }

  const config = pendingDecimalConfigs.shift();
  return config ?? null;
};

/**
 * Marks an API request to skip decimal conversion entirely
 * All decimal values will be preserved as raw values
 *
 * @example
 * skipDecimalConversion(ParticipantService.getPersonalDetails())
 */
export const skipDecimalConversion = <T>(request: Promise<T>): Promise<T> => {
  // Attach metadata directly to the promise object
  (request as any).__decimalConfig = {
    skipDecimalConversion: true,
  } as DecimalConversionConfig;

  return request;
};

/**
 * Marks specific fields in an API response to skip decimal conversion
 * All other decimal values will be converted to ACCEPT_DECIMAL places
 *
 * @param request - The API request promise
 * @param fields - Array of field paths to exclude (e.g., ['exchangeRate', 'items[].ratio'])
 *
 * @example
 * excludeDecimalFields(
 *   TransactionService.getHistory(),
 *   ['exchangeRate', 'conversionRatio', 'items[].ratio']
 * )
 */
export const excludeDecimalFields = <T>(request: Promise<T>, fields: string[]): Promise<T> => {
  // Attach metadata directly to the promise object
  (request as any).__decimalConfig = {
    excludeDecimalFields: fields,
  } as DecimalConversionConfig;

  return request;
};

/**
 * Get decimal conversion config from an axios request config
 * Used internally by the API interceptor
 * @deprecated Prefer `resolveDecimalConfig` (includes URL rules)
 */
export const getDecimalConfig = (request: any): DecimalConversionConfig => {
  return request?.__decimalConfig || {};
};
